import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const PALETTES = {
  B: { key: 'B', primary: '#FF5C5C', primaryShadow: '#B83838', accent: '#FFD23F', accent2: '#3DC9B5', coral: '#FF5C5C', plum: '#5B2E5E', aqua: '#3DC9B5', ink: '#2A1F3D', cream: '#FFF4E6', sand: '#FFE4C4', mutedText: '#7A6B85', heroStart: '#FFB86B', heroEnd: '#FF5C5C' },
  A: { key: 'A', primary: '#2A6B30', primaryShadow: '#1E5C2E', accent: '#E8A020', accent2: '#67A844', coral: '#2A6B30', plum: '#1E5C2E', aqua: '#67A844', ink: '#3E2B1E', cream: '#F8F6F0', sand: '#F0EAD6', mutedText: '#A67C52', heroStart: '#EAF2EA', heroEnd: '#1E5C2E' },
  C: { key: 'C', primary: '#5B4EC7', primaryShadow: '#3B2F9E', accent: '#FFD23F', accent2: '#16A085', coral: '#5B4EC7', plum: '#3B2F9E', aqua: '#16A085', ink: '#1A1A2E', cream: '#F5F3FF', sand: '#EEE9FF', mutedText: '#6A6A80', heroStart: '#B8A9FF', heroEnd: '#5B4EC7' },
};
const FONT = '"Nunito", "Baloo 2", sans-serif';
const Owl = ({ size = 30 }) => <div style={{ fontSize: size * 0.85, lineHeight: 1 }}>🦉</div>;

const TAG_TYPE_ICONS = { vocabulary: '📖', grammar: '✏️', vocabulary_topic: '🏷️' };

const PaletteSwitcher = ({ current, onChange }) => (
  <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 1000, display: 'flex', gap: 8, alignItems: 'center', background: 'rgba(255,255,255,0.95)', padding: '8px 12px', borderRadius: 999, border: '2px solid rgba(0,0,0,0.15)', boxShadow: '0 4px 12px rgba(0,0,0,0.12)', fontFamily: 'system-ui, sans-serif' }}>
    <span style={{ fontSize: 12, fontWeight: 700, color: '#1a1a2e' }}>Màu:</span>
    {Object.values(PALETTES).map(p => {
      const active = current === p.key;
      return <button key={p.key} onClick={() => onChange(p.key)} style={{ width: 26, height: 26, borderRadius: '50%', background: `linear-gradient(135deg, ${p.heroStart}, ${p.heroEnd})`, border: active ? `3px solid ${p.ink}` : '2px solid rgba(0,0,0,0.15)', boxShadow: active ? '0 0 0 2px #fff' : 'none', cursor: 'pointer', padding: 0, transform: active ? 'scale(1.1)' : 'scale(1)' }} />;
    })}
  </div>
);

// ─── Tag detail popup (Style B) ─────────────────────────────────
const TagDetailPopup = ({ tag, tagType, onClose, P }) => {
  const content = tag.tag_content || {};
  const nameVi = content.name?.vi;
  const nameEn = content.name?.en || tag.tag_name;
  const definition = content.definition?.vi || content.definition?.en;
  const usage = content.usage?.vi || content.usage?.en;
  const example = content.example?.vi || content.example?.en;
  const description = content.description?.vi || content.description?.en;
  const level = content.level;
  const formula = content.formula;
  const pos = content.pos;

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 2000, padding: 16,
      background: 'rgba(42,31,61,0.55)', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 22, padding: '28px 30px',
        maxWidth: 480, width: '100%', maxHeight: '85vh', overflowY: 'auto',
        border: `3.5px solid ${P.ink}`, boxShadow: `0 10px 0 ${P.ink}`,
        fontFamily: FONT, color: P.ink, position: 'relative',
      }}>
        <button onClick={onClose} style={{
          position: 'absolute', top: 14, right: 14,
          width: 32, height: 32, borderRadius: 10,
          background: '#fff', border: `2px solid ${P.ink}`, boxShadow: `0 2px 0 ${P.ink}`,
          cursor: 'pointer', fontSize: 16, fontWeight: 800, color: P.ink,
        }}>×</button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          <div style={{
            width: 48, height: 48, borderRadius: 12, background: P.accent,
            border: `2.5px solid ${P.ink}`, boxShadow: `0 3px 0 ${P.ink}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24,
          }}>{TAG_TYPE_ICONS[tagType] || '📋'}</div>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: P.ink }}>{nameEn}</div>
            {nameVi && nameVi !== nameEn && <div style={{ fontSize: 13, color: P.mutedText }}>{nameVi}</div>}
          </div>
        </div>

        {/* Score badge */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '6px 12px', borderRadius: 10, marginBottom: 14,
          background: tag.mastery_score >= 70 ? '#DFF5F0' : tag.mastery_score >= 40 ? P.sand : '#FFE4E4',
          border: `2px solid ${tag.mastery_score >= 70 ? P.aqua : tag.mastery_score >= 40 ? P.accent : P.coral}`,
          color: tag.mastery_score >= 70 ? '#0B8F7D' : tag.mastery_score >= 40 ? P.plum : P.primaryShadow,
          fontSize: 13, fontWeight: 800,
        }}>
          {tag.mastery_score >= 70 ? '🌟' : tag.mastery_score >= 40 ? '📈' : '🌱'} {tag.mastery_score}%
        </div>

        {/* Details */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13 }}>
          {level && <DetailRow icon="🎯" label="Cấp độ" value={level} P={P} />}
          {pos && <DetailRow icon="🔤" label="Loại từ" value={pos} P={P} />}
          {definition && <DetailRow icon="📝" label="Định nghĩa" value={definition} P={P} />}
          {description && !definition && <DetailRow icon="📝" label="Mô tả" value={description} P={P} />}
          {formula && <DetailRow icon="🧮" label="Công thức" value={formula} P={P} mono />}
          {usage && <DetailRow icon="💡" label="Cách dùng" value={usage} P={P} />}
          {example && <DetailRow icon="💬" label="Ví dụ" value={example} P={P} html />}
        </div>
      </div>
    </div>
  );
};

const DetailRow = ({ icon, label, value, P, mono, html }) => (
  <div style={{
    display: 'flex', gap: 10, padding: '10px 12px',
    background: P.cream, border: `2px solid ${P.ink}`, borderRadius: 10,
  }}>
    <div style={{ fontSize: 16 }}>{icon}</div>
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 10, fontWeight: 800, color: P.plum, letterSpacing: 1 }}>{label.toUpperCase()}</div>
      {html ? (
        <div style={{ fontSize: 13, fontWeight: 600, color: P.ink, marginTop: 2, lineHeight: 1.5 }}
          dangerouslySetInnerHTML={{ __html: value }} />
      ) : (
        <div style={{
          fontSize: 13, fontWeight: 600, color: P.ink, marginTop: 2, lineHeight: 1.5,
          fontFamily: mono ? 'monospace' : 'inherit',
        }}>{value}</div>
      )}
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════════
// Main Result component
// ═══════════════════════════════════════════════════════════════
const PlacementResult = () => {
  const navigate = useNavigate();
  const P = PALETTES.B;

  const [result, setResult] = useState(null);
  const [selectedTag, setSelectedTag] = useState(null);
  const [selectedTagType, setSelectedTagType] = useState(null);

  useEffect(() => {
    // Prefer sessionStorage (new adaptive flow), fallback to localStorage (legacy).
    const raw = sessionStorage.getItem('placement_result') || localStorage.getItem('placementResult');
    if (raw) {
      try { setResult(JSON.parse(raw)); }
      catch { navigate('/dashboard'); }
    } else {
      navigate('/dashboard');
    }
  }, [navigate]);

  if (!result) return null;

  const {
    total_correct, total_questions, strengths = [], weaknesses = [], all_tested_tags = [],
    level: cefrLevel, confidence, peak_level, peak_g_tier,
  } = result;
  const tagsByType = all_tested_tags.reduce((acc, group) => {
    const label = group.tag_type_label || group.tag_type_code || 'Khác';
    acc[label] = group.tags || [];
    return acc;
  }, {});

  const percent = total_questions > 0 ? Math.round((total_correct / total_questions) * 100) : 0;

  // Prefer CEFR level from adaptive flow; fallback to accuracy-based label.
  let levelLabel, levelEmoji, levelBg;
  if (cefrLevel) {
    levelLabel = `${cefrLevel}${confidence ? ' ' + confidence : ''}`;
    const emojiMap = { Starters: '🐣', Movers: '🐥', Flyers: '🦅' };
    levelEmoji = emojiMap[cefrLevel] || '🌟';
    levelBg = cefrLevel === 'Flyers' ? P.coral : cefrLevel === 'Movers' ? P.accent : P.aqua;
  } else if (percent >= 80) { levelLabel = 'Xuất sắc'; levelEmoji = '🏆'; levelBg = P.accent; }
  else if (percent >= 50) { levelLabel = 'Khá tốt'; levelEmoji = '🌟'; levelBg = P.aqua; }
  else { levelLabel = 'Đang phát triển'; levelEmoji = '🌱'; levelBg = P.sand; }

  const handleGoToDashboard = () => {
    localStorage.setItem('hasCompletedPlacement', 'true');
    navigate('/dashboard');
  };

  return (
    <>
      <div style={{ minHeight: '100vh', background: P.cream, fontFamily: FONT, color: P.ink, display: 'flex', flexDirection: 'column' }}>
        {/* Top bar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 28px', background: '#fff', borderBottom: `3px solid ${P.ink}`, flexWrap: 'wrap' }}>
          <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#fff', border: `2.5px solid ${P.ink}`, boxShadow: `0 3px 0 ${P.ink}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Owl size={30} />
          </div>
          <div style={{ fontSize: 18, fontWeight: 800, color: P.ink }}>HuPath</div>
          <div style={{ width: 1, height: 22, background: P.ink, opacity: 0.2 }} />
          <div style={{ background: P.accent, color: P.ink, border: `2px solid ${P.ink}`, borderRadius: 8, padding: '4px 10px', fontSize: 10, fontWeight: 800, letterSpacing: 1.5 }}>🎊 KẾT QUẢ</div>
          <div style={{ flex: 1, display: 'flex', justifyContent: 'center', gap: 6 }}>
            {[0, 1, 2, 3].map(i => <div key={i} style={{ width: 32, height: 8, borderRadius: 4, background: P.coral, border: `2px solid ${P.ink}` }} />)}
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, color: P.ink, opacity: 0.6 }}>Xong rồi!</div>
        </div>

        <div className="flex flex-col lg:flex-row" style={{ flex: 1 }}>
          {/* LEFT — big result hero */}
          <div style={{
            flex: 1, padding: '40px 48px',
            background: `linear-gradient(160deg, ${P.coral} 0%, ${P.plum} 100%)`,
            color: '#fff', display: 'flex', flexDirection: 'column', justifyContent: 'center',
            position: 'relative', overflow: 'hidden', minHeight: 500,
          }}>
            {['🎉', '⭐', '✨', '🎊'].map((e, i) => (
              <div key={i} style={{
                position: 'absolute',
                top: `${8 + (i * 21) % 75}%`, left: `${6 + (i * 29) % 88}%`,
                fontSize: 26 + (i % 3) * 8, opacity: 0.3,
                transform: `rotate(${i * 50}deg)`, pointerEvents: 'none',
              }}>{e}</div>
            ))}

            <div style={{ fontSize: 13, fontWeight: 800, color: P.accent, letterSpacing: 3 }}>
              {levelEmoji} KẾT QUẢ BÀI KIỂM TRA
            </div>
            <div style={{ fontSize: 20, fontWeight: 700, marginTop: 10, opacity: 0.9 }}>
              Chúc mừng! Hu đã tìm thấy trình độ phù hợp cho bé:
            </div>

            {/* big level badge */}
            <div style={{
              marginTop: 24, background: '#fff', color: P.ink,
              borderRadius: 24, padding: '24px 28px',
              border: `3.5px solid ${P.ink}`, boxShadow: `0 8px 0 ${P.ink}`,
              display: 'flex', alignItems: 'center', gap: 20,
            }}>
              <div style={{
                width: 84, height: 84, borderRadius: 20, background: levelBg,
                border: `3px solid ${P.ink}`, boxShadow: `0 4px 0 ${P.ink}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 44, flexShrink: 0,
              }}>{levelEmoji}</div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 800, color: P.plum, letterSpacing: 2 }}>
                  {cefrLevel ? 'TRÌNH ĐỘ HIỆN TẠI' : 'KẾT QUẢ'}
                </div>
                <div style={{ fontSize: 40, fontWeight: 800, color: P.ink, letterSpacing: -1 }}>{levelLabel}</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: P.ink, opacity: 0.7 }}>
                  {total_correct}/{total_questions} câu · {percent}%
                  {peak_level ? ` · peak C${peak_level}` : ''}{peak_g_tier ? ` G${peak_g_tier}` : ''}
                </div>
              </div>
            </div>

            <div style={{ marginTop: 18, display: 'flex', gap: 10 }}>
              {[
                { l: 'ĐÃ LÀM', v: `${total_questions} câu` },
                { l: 'ĐÚNG', v: `${total_correct}/${total_questions}` },
                { l: 'THƯỞNG', v: '+50 🪙' },
              ].map((s, i) => (
                <div key={i} style={{
                  background: 'rgba(255,255,255,0.15)', border: `2px solid rgba(255,255,255,0.3)`,
                  borderRadius: 12, padding: '10px 14px',
                  textAlign: 'center', flex: 1, minWidth: 80,
                }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: P.accent, letterSpacing: 1 }}>{s.l}</div>
                  <div style={{ fontSize: 22, fontWeight: 800, marginTop: 2 }}>{s.v}</div>
                </div>
              ))}
            </div>

            <button onClick={handleGoToDashboard} style={{
              marginTop: 24, padding: '16px', border: `3px solid ${P.ink}`,
              background: P.accent, color: P.ink,
              fontFamily: FONT, fontSize: 17, fontWeight: 800,
              borderRadius: 16, boxShadow: `0 6px 0 ${P.ink}`, cursor: 'pointer',
            }}>🚀 Vào trang học của con</button>
          </div>

          {/* RIGHT — breakdown */}
          <div style={{ flex: 1.1, padding: '36px 40px', overflowY: 'auto' }}>
            {/* Strengths */}
            {strengths.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: P.plum, letterSpacing: 2 }}>💪 ĐIỂM MẠNH</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: P.ink, marginBottom: 12, marginTop: 2 }}>Con làm tốt ở đây</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {strengths.map((tag, i) => (
                    <span key={i} style={{
                      padding: '6px 14px', background: '#DFF5F0',
                      border: `2px solid ${P.aqua}`, borderRadius: 99,
                      fontSize: 13, fontWeight: 800, color: '#0B8F7D',
                    }}>{tag.tag_name || tag}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Weaknesses */}
            {weaknesses.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: P.plum, letterSpacing: 2 }}>📚 CẦN LUYỆN</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: P.ink, marginBottom: 12, marginTop: 2 }}>Hu sẽ giúp con ôn</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {weaknesses.map((tag, i) => (
                    <span key={i} style={{
                      padding: '6px 14px', background: '#FFE4E4',
                      border: `2px solid ${P.coral}`, borderRadius: 99,
                      fontSize: 13, fontWeight: 800, color: P.primaryShadow,
                    }}>{tag.tag_name || tag}</span>
                  ))}
                </div>
              </div>
            )}

            {/* All tested tags */}
            {Object.keys(tagsByType).length > 0 && (
              <div style={{
                background: '#fff', border: `2.5px solid ${P.ink}`, boxShadow: `0 5px 0 ${P.ink}`,
                borderRadius: 16, padding: 20, marginBottom: 20,
              }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: P.ink, marginBottom: 12 }}>📋 Các kỹ năng đã đánh giá</div>
                {Object.entries(tagsByType).map(([groupName, tags]) => {
                  const typeCode = (all_tested_tags.find(g => (g.tag_type_label || g.tag_type_code) === groupName) || {}).tag_type_code || '';
                  return (
                    <div key={groupName} style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 11, fontWeight: 800, color: P.mutedText, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
                        {TAG_TYPE_ICONS[typeCode] || '📋'} {groupName}
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {tags.map(tag => {
                          const score = tag.mastery_score ?? 0;
                          const bg = score >= 70 ? '#DFF5F0' : score >= 40 ? P.sand : '#FFE4E4';
                          const bc = score >= 70 ? P.aqua : score >= 40 ? P.accent : P.coral;
                          return (
                            <button key={tag.tag_id} onClick={() => { setSelectedTag(tag); setSelectedTagType(typeCode); }} style={{
                              display: 'inline-flex', alignItems: 'center', gap: 6,
                              padding: '5px 10px', background: bg, border: `1.5px solid ${bc}`,
                              borderRadius: 99, fontFamily: FONT, fontSize: 12, fontWeight: 800, color: P.ink,
                              cursor: 'pointer',
                            }}>
                              <span>{TAG_TYPE_ICONS[typeCode] || '📋'}</span>
                              {tag.tag_name}
                              <span style={{ opacity: 0.7 }}>{score}%</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
                <div style={{ display: 'flex', gap: 12, fontSize: 11, fontWeight: 700, color: P.mutedText, flexWrap: 'wrap' }}>
                  <span>🟢 Tốt (≥70%)</span><span>🟡 Khá (≥40%)</span><span>🔴 Cần luyện</span>
                </div>
              </div>
            )}

            {/* Info */}
            <div style={{
              padding: '12px 14px', background: P.sand,
              border: `2px dashed ${P.ink}`, borderRadius: 10,
              fontSize: 13, fontWeight: 700, color: P.ink, lineHeight: 1.4,
              display: 'flex', gap: 10, alignItems: 'flex-start',
            }}>
              <span style={{ fontSize: 20 }}>🤖</span>
              <span>HuPath đã tạo lộ trình cá nhân hoá dựa vào kết quả này. Có thể làm lại placement sau 30 ngày để cập nhật trình độ.</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tag detail popup */}
      {selectedTag && (
        <TagDetailPopup
          tag={selectedTag}
          tagType={selectedTagType}
          onClose={() => { setSelectedTag(null); setSelectedTagType(null); }}
          P={P}
        />
      )}
    </>
  );
};

export default PlacementResult;
